namespace Solovis.Reports.ReportHelpers.ReportBuilderHelpers {

  /**
   * Requests the parameters for a report by name.
   * @param reportName
   */
  export async function getParametersAsync(reportName: string): Promise<any> {
    return Solovis.Rpc.invoke("~/Rpc/Reporting/GetReportParameters",
      { name: reportName },
      function (response, success) { })
      .then(result => {
        const params = result.result;
        params.forEach(p => {
          if (p.visibilityDependsOn?.parameterNameIsTrue) {
            p.visible = function () {
              return this.getParameterValue(p.visibilityDependsOn.parameterNameIsTrue);
            }
          }
          else if (p.visibilityDependsOn?.otherParameterValue) {
            p.visible = function () {
              return this.getParameterValue(p.visibilityDependsOn.otherParameterValue.parameterName)
               == p.visibilityDependsOn.otherParameterValue.value;
            }
          }
        });
        return params;
      })
      .catch(err => {
        Solovis.Rpc.showErrorDialog(err.error);
        throw err;
      });
  }

  /**
   * Gets the data sources for a report builder report.
   * Returns two data sources: "report" and "modelEnv".
   * @param reportName
   */
  export function getDataSources(reportName: string): Solovis.Reports.DataSourceDefinition[] {
    return [
      {
        name: "report",
        customDataAsync: async (callback, more) => {
          Solovis.Rpc.invoke("~/Rpc/Reporting/LoadReport/" + reportName,
            {
              options: more.parameters,
            },
            function (response, success) {
              if (success) {
                // TODO: consider moving these concerns server-side
                response.result.reportSet?.lineItems?.forEach((li, i) => li.index = i);
                response.result.reportSet?.dataColumns?.forEach((c) => {
                  c.field = "data." + c.id;
                  c.dataType = c.dataType.charAt(0).toLowerCase() + c.dataType.slice(1);
                });
                callback({ dataSet: response.result });
              } else {
                callback(response);
              }
            })
            .catch((err) => { console.error(err); });
        }
      },
      {
        name: "modelEnv",
        path: "/Solovis/Metadata/ModelEnvironment",
        parameterMap: function (parameters, more) {
          const propIds = parameters?.propValuesToInclude?.includeList?.map(x => x.propDefId);
          return {
            propertyDefinitionOptions: { propertyDefinitionIdsToLoad: propIds }
          };
        },
        postProcess: function (loadedDataSet, more) {
          var modelEnv = new InvData.ModelEnvironment();
          modelEnv.mergeEnvironmentData(loadedDataSet.modelEnvironment);
          return {
            modelEnv: modelEnv,
          };
        }
      }
    ]
  }

  /**
   * Requests the parameters for a report by name.
   * Expects resourceId, holdingResourceId, ownerResourceId on the rows inside "data"
   */
  export function getCellContextMenuItems(cellContext: any): Solovis.UI.PopupLinkPanelLink[] {
    let row = cellContext.row;
    let rowToUse = row.data;
    if (row._backingRow) {
      rowToUse = row._backingRow.data;
    }

    let items = [];
    if (rowToUse.resourceId) {
      items.push({
        label: "Asset Info",
        href: getAssetInfoLinkFromResourceId(rowToUse.resourceId)
      });

      if (rowToUse.holdingResourceId) {
        items.push({
          label: "Holding Info",
          href: getAssetInfoLinkFromResourceId(rowToUse.holdingResourceId)
        });
      }
      if (rowToUse.ownerResourceId) {
        items.push({
          label: "Owner Info",
          href: getAssetInfoLinkFromResourceId(rowToUse.ownerResourceId)
        });
      }
    }
    return items;
  }


  /**
   * Generates a header div given label value pairs.
   * @param headers
   */
  export function getHeaderDiv(headers: { label: string, value: string }[]): JQuery {
    var boxDiv = $("<div>").css({
      "border": "1px solid",
      "padding": "4px",
      "margin": "10px 0px"
    });
    let flexRow = $("<div>").css({
      "display": "flex",
      "flex-direction": "row",
      "gap": "20px"
    }).appendTo(boxDiv);

    function labelPairDivGenerator(label, value) {
      const pairDiv = $("<div>").css({
        "display": "flex",
        "flex-direction": "row",
        "gap": "10px"
      }).appendTo(flexRow);
      $("<div>").css({
        "font-weight": "bold",
        "color": "gray",
      }).text(label).appendTo(pairDiv);
      $("<div>").css("font-weight", "600").text(value).appendTo(pairDiv);
    }

    headers.forEach(headerCmpt => labelPairDivGenerator(headerCmpt.label, headerCmpt.value));
    return boxDiv;
  }


  export function getStandardTableSettings(getDataColumns: (dataTable: Solovis.Reports.Parts.Tables.DataTableControl) => Array<any>): Solovis.Reports.Parts.PartControl["settings"] {
    return {
      groupBy: [{ id: "all" }],
      aggregateRow: { display: "inline", groupHeaderColspan: 1 },
      columns: function () {
        let columns = getDataColumns(this);
        columns.forEach(c => {
          if (c.id == "assetLabel") {
            c.width = 300;
            c.cellStyle = function (cellValue, cellContext) {
              return cellContext?.row?.isBenchmark ? "font-style: italic;" : "";
            }
            c.htmlFormatter = function (cellValue, cellContext) {
              const defaultHtml = (Solovis as any).Reports.Formatter.formatValueAsHtml(cellValue, cellContext);
              const lineItem = cellContext?.row?._backingRow ?? cellContext?.row;
              if (!lineItem?.data?.isProFormaScaffold) {
                return defaultHtml;
              }
              // Pro Forma group-level scaffold row: a help icon explains it is a modeling-only
              // entry point for a trade at the grouping level, not a real holding.
              const span = $("<span>").append(defaultHtml);
              $("<a>")
                .addClass("fa fa-question-circle")
                .css("margin-left", "4px")
                .solovisTooltip({
                  text: "Non-investment/holding specific asset class level pro forma trades",
                })
                .appendTo(span);
              return span;
            }
          }
        });
        return columns;
      },
      subRowGroupKeyGetter: function (row) {
        return row.index;
      },
      groups: function () {
        const groups = [{
          id: "all",
          label: "All",
          dataRowsForGroups: true,
          field: "data.compositeParentGroupNodeId",
          groupValueField: "data.compositeGroupNodeId",
          htmlFormatter: Solovis.Reports.Formatter.assetLabelGroupHtmlFormatter
        }];
        return groups;
      },
      modelEnvironment: { path: "modelEnv.modelEnv" },
      cellContextMenuItems: Solovis.Reports.ReportHelpers.ReportBuilderHelpers.getCellContextMenuItems,
    };
  }

  function getAssetInfoLinkFromResourceId(resourceId) {
    return Solovis.Reports.ReportLinker.getReportLink({
      reportAlias: "AssetInfo",
      parameters: {
        entityOrInvestment: { resourceId: resourceId }
      },
      autoRun: true,
      collapseConfigSection: true,
    })
  }
}